import React from 'react';
import { Link } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import '../styles/BlogsPage.css';

export default function PrivacyPolicyPage() {
  return (
    <div className="blogs-page">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg blogs-navbar">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
          </Link>
          <div className="d-flex align-items-center gap-3">
            <Link to="/blogs" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-newspaper me-2"></i>Blog
            </Link>
            <Link to="/" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-arrow-left me-2"></i>Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="blog-detail-hero">
        <div className="container">
          <h1 className="blog-detail-title">Privacy Policy</h1>
          <p className="blog-detail-excerpt">Last Updated: March 25, 2026</p>
        </div>
      </header>

      {/* Content */}
      <article className="container blog-detail-content">
        <div className="blog-detail-body privacy-body">
          <p>
            At MeetingPoint.chat, we believe that privacy is a fundamental human right. This Privacy Policy outlines how we handle data, our commitment to a "Zero-Knowledge" architecture, and how third-party services (like Google AdSense) interact with our platform.
          </p>

          <h3>1. Our "No-Data" Philosophy</h3>
          <p>Unlike traditional communication platforms, MeetingPoint is built on a decentralized Peer-to-Peer (P2P) architecture.</p>
          <ul>
            <li><strong>No Accounts:</strong> We do not require or store names, email addresses, or phone numbers.</li>
            <li><strong>No Chat Logs:</strong> Conversations, video streams, and file transfers occur directly between users. Our servers do not intercept, view, or store the content of your communications.</li>
            <li><strong>Ephemeral Nature:</strong> Connection metadata is held in temporary memory only to facilitate the "handshake" between peers and is purged immediately upon disconnection.</li>
          </ul>

          <h3>2. Information We Collect Automatically</h3>
          <p>While we do not collect personal identities, certain technical information is processed to ensure the site functions correctly:</p>
          <ul>
            <li><strong>Log Data:</strong> Like most websites, our servers may log your IP address, browser type, and time of access for security and anti-abuse purposes. This data is not linked to any personal identity.</li>
            <li><strong>WebRTC Signaling:</strong> To connect you to another user, our signaling server temporarily processes IP addresses to establish the peer-to-peer tunnel. This data is not stored in a persistent database.</li>
          </ul>

          <h3>3. Google AdSense and Cookies</h3>
          <p>To keep this service free, we use Google AdSense to serve advertisements. Google's use of advertising cookies enables it and its partners to serve ads to our users based on their visit to this site and other sites on the Internet.</p>
          <ul>
            <li><strong>Personalized Advertising:</strong> Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to your website or other websites.</li>
            <li><strong>Opt-Out:</strong> Users may opt out of personalized advertising by visiting <a href="https://adssettings.google.com" target="_blank" rel="noreferrer" className="text-decoration-underline">Google Ads Settings</a>.</li>
            <li><strong>Third-Party Cookies:</strong> Other third-party ad networks may also use cookies on our site. You can opt out of some third-party vendors' uses of cookies for personalized advertising by visiting <a href="https://www.aboutads.info" target="_blank" rel="noreferrer" className="text-decoration-underline">www.aboutads.info</a>.</li>
          </ul>

          <h3>4. Data Security</h3>
          <p>We employ industry-standard encryption (DTLS and SRTP) for all P2P streams. However, users should be aware that in a P2P connection, your IP address is inherently shared with the person you are directly connecting to. We recommend using a VPN if you wish to mask your IP from your peer.</p>

          <h3>5. GDPR Data Protection Rights</h3>
          <p>We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:</p>
          <ul>
            <li><strong>The right to access:</strong> You have the right to request copies of your personal data. (Note: Since we do not store accounts, we generally hold no personal data to provide).</li>
            <li><strong>The right to rectification:</strong> You have the right to request that we correct any information you believe is inaccurate.</li>
            <li><strong>The right to erasure:</strong> You have the right to request that we erase your personal data, under certain conditions.</li>
          </ul>

          <h3>6. CCPA Privacy Rights</h3>
          <p>Under the CCPA, California consumers have the right to:</p>
          <ul>
            <li>Request disclosure of categories and specific pieces of personal data collected.</li>
            <li>Request deletion of any personal data collected.</li>
            <li>Request that a business not sell their personal data.</li>
          </ul>
          <p>MeetingPoint does not sell personal data. We do not collect personal identities, so there is no data to sell.</p>

          <h3>7. Children's Privacy</h3>
          <p>MeetingPoint is intended for use by individuals aged 18 and older. We do not knowingly collect or solicit any information from anyone under the age of 18.</p>

          <h3>8. Changes to This Policy</h3>
          <p>We reserve the right to update this Privacy Policy at any time. Changes will be reflected with an updated "Last Updated" date. Your continued use of the Service after changes constitutes acceptance of the revised policy.</p>

          <h3>9. Contact Us</h3>
          <p>If you have questions regarding this policy, you may contact the administrative team at:</p>
          <p><strong>Email:</strong> <a href="mailto:admin@meetingpoint.chat" className="text-decoration-underline">admin@meetingpoint.chat</a></p>
        </div>
      </article>

      {/* Footer */}
      <footer className="blogs-footer">
        <div className="container text-center">
          <p className="mb-1">&copy; {new Date().getFullYear()} MeetingPoint. All rights reserved.</p>
          <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
            <Link to="/" className="text-muted">Home</Link>
            <Link to="/about" className="text-muted">About</Link>
            <Link to="/blogs" className="text-muted">Blog</Link>
            <Link to="/faq" className="text-muted">FAQ</Link>
            <Link to="/contact" className="text-muted">Contact</Link>
            <Link to="/terms" className="text-muted">Terms</Link>
            <Link to="/disclaimer" className="text-muted">Disclaimer</Link>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}
